/*
 * BCM-891 — why the Planned Allocated Qty. edit button is greyed out.
 *
 * The API collapses every cause into one boolean (scheduleLineDetails
 * .editPlannedAllocation), so the reason has to be re-derived here from what
 * the browser can actually see. The spec lists five reasons and says the first
 * applicable one wins; these are the ones that are decidable client-side:
 *
 *   1. ROLE_NO_EDIT     — featuresAccess.editPlannedAllocatedQty is false.
 *   2. BRAND_MISSING    — the user holds the PO's CMO and product type but not
 *                         its brand.
 *   3. MULTIPLE_MISSING — more than one of those is missing.
 *   4. WINDOW_CLOSED /  — the allocation window configured on the product has
 *      WINDOW_NOT_OPEN    closed, or has not opened yet. The spec's reason 4 is
 *                         "PO status OR delivery date is outside the allocation
 *                         window", which is these two cases; they are kept apart
 *                         because "closed on <date>" is false before the window
 *                         has opened.
 *
 *   5. DOCUMENTS_RECEIVED — batch documents have been ingested against this PO.
 *                         Offered last and only as a fall-through, because
 *                         enableLotAllocation (the rule that actually produces
 *                         editPlannedAllocation, in the Corda PO-dashboard
 *                         flows) contains no document term: received documents
 *                         are never provably the cause. See the note at the
 *                         fall-through for why it is still worth showing.
 *
 * Showing a confident wrong explanation is worse than showing none, so when
 * nothing can be established this returns null and the caller leaves the button
 * bare.
 *
 * Plant is part of the spec's checklist but fetchUserAssociationsDirect returns
 * only brands, CMOs and product types, so plant is reported as 'unknown' rather
 * than being asserted either way.
 */

export const LOCK_REASONS = {
  ROLE_NO_EDIT: 'ROLE_NO_EDIT',
  BRAND_MISSING: 'BRAND_MISSING',
  // One association missing that is NOT the brand — the CMO alone, or the
  // product type alone. The spec covers "brand alone" and "more than one" and
  // skips this in between, so a user whose product type had been removed saw a
  // disabled button and no tooltip at all. Same sentence shape as the brand
  // case, naming whichever association it actually is.
  ASSOCIATION_MISSING: 'ASSOCIATION_MISSING',
  MULTIPLE_MISSING: 'MULTIPLE_MISSING',
  WINDOW_CLOSED: 'WINDOW_CLOSED',
  WINDOW_NOT_OPEN: 'WINDOW_NOT_OPEN',
  // The product says the other party does the allocating
  // (allowLotAllocationBy). Provable from the configuration, and previously
  // fell through to nothing.
  OWNED_BY_OTHER_SIDE: 'OWNED_BY_OTHER_SIDE',
  DOCUMENTS_RECEIVED: 'DOCUMENTS_RECEIVED',
  // Last resort. Nothing above applies, yet the button is disabled, so the
  // cause is in data this screen cannot see. States that much rather than
  // leaving the button bare — which is what the user actually complains about.
  UNKNOWN: 'UNKNOWN',
};

/*
 * The request each tooltip action raises, as named by
 * /requestBatchAllocationAccessDirect. Kept beside the reasons so the two
 * cannot drift apart.
 */
export const REASON_REQUEST_TYPE = {
  [LOCK_REASONS.ROLE_NO_EDIT]: 'EDIT_ACCESS',
  [LOCK_REASONS.BRAND_MISSING]: 'BRAND_ACCESS',
  [LOCK_REASONS.ASSOCIATION_MISSING]: 'ASSOCIATION_ACCESS',
  [LOCK_REASONS.MULTIPLE_MISSING]: 'ASSOCIATION_ACCESS',
  [LOCK_REASONS.WINDOW_CLOSED]: 'PRODUCT_SETUP',
  [LOCK_REASONS.WINDOW_NOT_OPEN]: 'PRODUCT_SETUP',
  [LOCK_REASONS.OWNED_BY_OTHER_SIDE]: 'ALLOCATION_OWNERSHIP',
  [LOCK_REASONS.DOCUMENTS_RECEIVED]: 'DOCUMENT_ISSUE',
  [LOCK_REASONS.UNKNOWN]: 'ALLOCATION_BLOCKED',
};

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Format an ISO timestamp for display in a tooltip. Returns the raw value when
 * it cannot be parsed, so a surprising format is shown rather than swallowed.
 */
export const formatWindowDate = (value, withTime = false) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  // DD-MMM-YYYY uppercase — the app's convention
  // (dateFormated in functionalComponents/dataFormatFunctions). Written out
  // rather than taken from toLocaleDateString because newer ICU renders
  // September as "Sept" through Intl, which is not the three-letter form used
  // everywhere else in the product; and rather than from moment so this module
  // stays dependency-free and its wording can be tested with plain Node.
  // A date alone (a delivery or close date) is a calendar day held at UTC
  // midnight, so it is read in UTC — west of Greenwich the local reading would
  // move it to the day before.
  const date = `${pad2(d.getUTCDate())}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
  if (!withTime) return date;
  // Times are shown in UTC as well, labelled as such, so every lock tooltip
  // reads in the same zone as the window close ("23:59 UTC") instead of mixing
  // UTC with the viewer's local "GMT+5:30".
  return `${date} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`;
};

/*
 * When the window actually shuts. The middleware allows allocation while
 * CURRENT_DATE <= delivery date - days, so the close date itself is still open
 * and the window ends as that day ends (UTC, the database's clock).
 */
export const windowClosesAt = (windowCloseDate) => {
  const d = new Date(windowCloseDate);
  if (Number.isNaN(d.getTime())) return NaN;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
};
export const formatWindowCloseMoment = (windowCloseDate) =>
  windowCloseDate ? `${formatWindowDate(windowCloseDate)}, 23:59 UTC` : '';

/*
 * Whether the spec gives this reason an action that opens the user's own access
 * list, alongside the request. Only the multiple-missing case does.
 */
/*
 * Whether the spec gives this reason an action that opens the user's own access
 * list, alongside the request.
 *
 * The spec's Actions column names "View my access" against exactly one reason —
 * "User is missing more than one association of the PO" — so it appears there
 * and nowhere else. It was briefly offered on the single-gap reason too; that
 * was an addition the spec does not make, and it has been taken back out.
 */
export const reasonHasViewMyAccess = (reason) => reason === LOCK_REASONS.MULTIPLE_MISSING;

/** The action label the spec gives each reason. */
export const REASON_ACTION_LABEL = {
  [LOCK_REASONS.ROLE_NO_EDIT]: 'Request edit access',
  [LOCK_REASONS.BRAND_MISSING]: 'Request brand access',
  [LOCK_REASONS.ASSOCIATION_MISSING]: 'Request access',
  [LOCK_REASONS.MULTIPLE_MISSING]: 'Request access',
  [LOCK_REASONS.WINDOW_CLOSED]: 'Ask an admin about product setup',
  [LOCK_REASONS.WINDOW_NOT_OPEN]: 'Ask an admin about product setup',
  [LOCK_REASONS.OWNED_BY_OTHER_SIDE]: 'Ask an admin about product setup',
  [LOCK_REASONS.DOCUMENTS_RECEIVED]: 'Report an issue',
  [LOCK_REASONS.UNKNOWN]: 'Ask an admin about product setup',
};

const ACCESS_NOUNS = {
  cmo: 'CMO',
  plant: 'plant',
  brand: 'brand',
  productType: 'product type',
};

// "a, b and c" — the spec's phrasing for the association lists.
const joinWords = (words) => {
  const list = words.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

const COUNT_WORDS = { 2: 'Two', 3: 'Three', 4: 'Four' };

/**
 * The tooltip heading and message for a resolved lock, as plain strings.
 *
 * Kept out of the component so the wording can be asserted against the spec
 * table directly. Placeholders the spec quotes are filled from `ctx`; where a
 * value is genuinely not held the clause naming it is dropped rather than
 * printed empty, so no sentence ever contains a blank quote.
 *
 * @param {object} lock  as returned by resolveLockReason
 * @param {object} ctx   { roleName, poNumber, productCode, brand, cmoName,
 *                         productType, batchNumber }
 * @returns {{heading: string, message: string}}
 */
export const buildLockMessage = (lock, ctx = {}) => {
  const q = (v) => `'${v}'`;
  const poRef = ctx.poNumber ? `PO ${q(ctx.poNumber)}` : 'this PO';
  // The spec writes "PO '<PO Number>'s delivery date" — the s sits outside the
  // closing quote. Writing `${poRef}'s` instead would produce "PO '451''s".
  const poPossessive = ctx.poNumber ? `PO ${q(ctx.poNumber)}s` : "this PO's";
  const productClause = ctx.productCode ? ` (product ${q(ctx.productCode)})` : '';

  switch (lock?.reason) {
    case LOCK_REASONS.ROLE_NO_EDIT:
      return {
        heading: "Your role can't edit batch allocation",
        message:
          `You're signed in as a ${q(ctx.roleName || 'user')}, and that role has view-only `
          + `access to batch allocation. You can view and download ${poRef}, but you can't `
          + 'change its planned allocated quantity.',
      };

    case LOCK_REASONS.BRAND_MISSING: {
      // The spec lists CMO, plant and product type as held. Only the ones this
      // user is actually confirmed to hold are named — plant is never returned
      // by the associations API, so claiming it would be invention.
      const held = ['cmo', 'plant', 'productType']
        .filter((k) => lock.checklist?.[k] === 'available')
        .map((k) => ACCESS_NOUNS[k]);
      const brandClause = `its brand${ctx.brand ? `, ${q(ctx.brand)}` : ''}`;
      const heldClause =
        held.length > 0
          ? `you have access to its ${joinWords(held)} — but not ${brandClause}`
          : `you do not have access to ${brandClause}`;
      return {
        heading: 'Brand access missing for this PO',
        message:
          `For ${poRef}${productClause}, ${heldClause}. That's the only thing missing `
          + 'before you can edit this allocation.',
      };
    }

    /*
     * Exactly one association missing, and it is not the brand.
     *
     * The spec table jumps from "brand alone" to "more than one" and leaves
     * this gap, so a user whose product type had been removed got a disabled
     * button with no tooltip on it at all. Built from the brand-alone template
     * with the missing association named, so it reads the same way.
     */
    case LOCK_REASONS.ASSOCIATION_MISSING: {
      const key = (lock.missing || [])[0];
      const noun = ACCESS_NOUNS[key] || 'access';
      const names = { cmo: ctx.cmoName, brand: ctx.brand, productType: ctx.productType };
      const held = ['cmo', 'plant', 'brand', 'productType']
        .filter((k) => k !== key && lock.checklist?.[k] === 'available')
        .map((k) => ACCESS_NOUNS[k]);
      const missingClause = `its ${noun}${names[key] ? `, ${q(names[key])}` : ''}`;
      const heldClause =
        held.length > 0
          ? `you have access to its ${joinWords(held)} — but not ${missingClause}`
          : `you do not have access to ${missingClause}`;
      return {
        // "CMO access missing for this PO" / "Product type access missing for
        // this PO", matching the brand heading's shape.
        heading: `${noun === 'CMO' ? 'CMO' : noun.charAt(0).toUpperCase() + noun.slice(1)} access missing for this PO`,
        message:
          `For ${poRef}${productClause}, ${heldClause}. That's the only thing missing `
          + 'before you can edit this allocation.',
      };
    }

    case LOCK_REASONS.MULTIPLE_MISSING: {
      const missing = lock.missing || [];
      const held = ['cmo', 'plant', 'brand', 'productType']
        .filter((k) => lock.checklist?.[k] === 'available')
        .map((k) => ACCESS_NOUNS[k]);
      const names = { cmo: ctx.cmoName, brand: ctx.brand, productType: ctx.productType };
      const missingPhrases = missing.map(
        (k) => `its ${ACCESS_NOUNS[k]}${names[k] ? `, ${q(names[k])}` : ''}`
      );
      const heldClause = held.length > 0 ? `you have ${joinWords(held)} access, but not ` : 'you do not have ';
      return {
        heading: `${COUNT_WORDS[missing.length] || missing.length} accesses are missing for this PO`,
        message:
          `For ${poRef}${productClause}, ${heldClause}${missingPhrases.join(', or ')}. `
          + `An admin can add ${missing.length === 2 ? 'both' : 'all of them'} in one request.`,
      };
    }

    case LOCK_REASONS.WINDOW_CLOSED:
    case LOCK_REASONS.WINDOW_NOT_OPEN: {
      const w = lock.window || {};
      const startStatus = w.startPOStatusText || `status ${w.startPOStatusID}`;
      const days = Number(w.daysBeforeDelivery);
      const untilPhrase = !Number.isFinite(days)
        ? 'until shortly before delivery'
        : days === 0
        ? 'until its delivery date'
        : `until ${days === 1 ? 'one day' : `${days} days`} before delivery`;
      const productRef = ctx.productCode ? `Product ${q(ctx.productCode)}` : 'This product';
      const opening =
        `${productRef} allows batch allocation from ${q(startStatus)} status ${untilPhrase}.`;

      if (lock.reason === LOCK_REASONS.WINDOW_NOT_OPEN) {
        const current = w.currentPOStatusText || w.currentPOStatusID;
        return {
          heading: `Allocation opens at ${q(startStatus)}`,
          message:
            `${opening} ${poRef} is at ${q(current)}, so editing is not available yet.`,
        };
      }
      return {
        heading: `Allocation window closed on ${q(formatWindowDate(w.windowCloseDate))}`,
        message:
          `${opening} ${poPossessive} delivery date is ${q(formatWindowDate(w.deliveryDate))}, so its `
          + `allocation window closed on ${q(formatWindowCloseMoment(w.windowCloseDate))} — `
          + "that's why editing is off now.",
      };
    }

    /*
     * The product names the other party as the one who allocates
     * (allowLotAllocationBy). Read straight off the configuration, so this is
     * a fact rather than an inference — it previously fell through to nothing
     * and left the button bare.
     */
    case LOCK_REASONS.OWNED_BY_OTHER_SIDE: {
      const owner = lock.allocationOwner === 'CMO' ? 'the CMO' : 'BMS';
      const productRef = ctx.productCode ? `Product ${q(ctx.productCode)}` : 'This product';
      return {
        heading: `Batch allocation is done by ${owner}`,
        message:
          `${productRef} is set up so that only ${owner} can change batch allocation, `
          + `so editing is off for you on ${poRef}. An admin can change who allocates `
          + 'for this product.',
      };
    }

    /*
     * Nothing above applies and the button is still disabled, so the cause is
     * in data this screen cannot see. Everything it rules out IS known, so the
     * message says what has been checked rather than guessing at a reason.
     */
    case LOCK_REASONS.UNKNOWN:
      return {
        heading: 'Editing is switched off for this PO',
        message:
          `Batch allocation cannot be edited on ${poRef}${productClause} right now. `
          + 'Your role has edit access and nothing is missing from your associations, '
          + "so the reason sits in this product's allocation setup. An admin can check it.",
      };

    case LOCK_REASONS.DOCUMENTS_RECEIVED: {
      const docs = lock.documents || [];
      const sender = ctx.cmoName ? `CMO ${q(ctx.cmoName)}` : 'The CMO';
      const batchClause = ctx.batchNumber ? `, batch ${q(ctx.batchNumber)}` : '';
      const first = docs.find((d) => d.ingestedAt);
      const onClause = first ? `, on ${q(formatWindowDate(first.ingestedAt, true))}` : '';
      return {
        heading: 'Batch documents already received',
        message:
          `${sender} sent these batch documents for ${poRef}${batchClause}${onClause}. `
          + 'Allocation closes once documents are received, so editing is off for this batch.',
      };
    }

    default:
      return { heading: '', message: '' };
  }
};

const asText = (v) => String(v == null ? '' : v).trim().toLowerCase();
const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/*
 * 'available' | 'missing' | 'unknown' for one association.
 *
 * The spec's checklist has two states, available or missing, so 'unknown' is
 * kept for the one case where neither can honestly be claimed: the list was
 * never loaded at all, because the associations call failed or the user has no
 * associations row. The caller never accuses anyone on the strength of data it
 * does not have.
 *
 * A list that loaded and is EMPTY is not that case. An empty list means the
 * user is not restricted on that dimension, which is how the product itself
 * reads it — fetchPurchaseOrdersNewDirect applies the CMO, product-type and
 * brand filters only when the corresponding list is non-empty, so a user with
 * no product types recorded is shown POs of every product type. Reported as
 * available for that reason.
 *
 * Either way this cannot change which tooltip is chosen: available and unknown
 * both mean "not missing", and only 'missing' feeds the reason.
 */
const check = (list, matches) => {
  if (!Array.isArray(list)) return 'unknown';
  if (list.length === 0) return 'available';
  return list.some(matches) ? 'available' : 'missing';
};

/**
 * Compare the PO against the caller's own associations.
 * @returns {{cmo, brand, productType, plant}} each 'available' | 'missing' | 'unknown'
 */
export const computeAccessChecklist = (po = {}, associations = {}) => {
  const brandName = asText(po.brand);
  const cmoID = asNum(po.vendorMosymphonyID);
  const productTypeID = asNum(po.productTypeID);

  return {
    // Brand names round-trip through toSentenceCase in the middleware, so the
    // comparison is case-insensitive.
    brand: brandName
      ? check(associations.associatedBrandList, (b) => asText(b) === brandName)
      : 'unknown',
    cmo: cmoID === null
      ? 'unknown'
      : check(associations.associatedCMOList, (c) => asNum(c) === cmoID),
    productType: productTypeID === null
      ? 'unknown'
      : check(associations.associatedProductTypesList, (p) => asNum(p) === productTypeID),
    /*
     * Plant is not an association this system holds. user_associations carries
     * brands, CMOs and product types and nothing else, and no endpoint returns
     * a plant list, so a plant can never restrict anybody.
     *
     * That is why the spec's own sentences put plant among the accesses the
     * user HAS — "you have access to its CMO, plant and product type", "you
     * have plant and brand access". Reporting it as available says the true
     * thing (no plant blocks this user) and makes both sentences read exactly
     * as the spec writes them. Reporting 'unknown' dropped the word and left
     * the message permanently off-spec.
     *
     * It cannot change which tooltip is chosen: `missing` is computed from
     * cmo, brand and productType only, and plant is not among them.
     *
     * If a plant list is ever returned, it is used instead of this reasoning.
     */
    plant: Array.isArray(associations.associatedPlantList) && associations.associatedPlantList.length > 0
      ? check(associations.associatedPlantList, (p) => asNum(p) === asNum(po.plantID))
      : 'available',
  };
};

/*
 * Which half of the allocation window applies to the signed-in user.
 *
 * allowLotAllocationBy names who may allocate: 'Sponsor' and 'CMO' restrict it
 * to that collaborator type, 'all' lets either side allocate under its own
 * trigger pair. This mirrors enableLotAllocation in the Corda flow that
 * produces editPlannedAllocation. Returns null when the viewer's side has no
 * window of its own — allocation is then owned by the other party, which is a
 * different situation from a closed window and carries no spec message.
 */
const resolveWindowSide = (allocationWindow, collaboratorTypeID) => {
  if (!allocationWindow) return null;
  const type = Number(collaboratorTypeID);
  if (!Number.isFinite(type)) return null;
  const allowBy = String(allocationWindow.allowLotAllocationBy || '');
  const side = type === 1 ? allocationWindow.sponsor : type === 2 ? allocationWindow.cmo : null;
  if (!side) return null;
  if (allowBy === 'all') return side;
  if (allowBy === 'Sponsor' && type === 1) return side;
  if (allowBy === 'CMO' && type === 2) return side;
  return null;
};

/*
 * 'Sponsor' or 'CMO' when the product hands batch allocation to the party the
 * viewer is NOT, and null otherwise.
 *
 * allowLotAllocationBy is the first term of enableLotAllocation, so when it
 * names the other side that is the whole reason the button is off — no
 * inference involved. 'all' lets either side allocate and therefore never
 * blocks anyone. A viewer whose collaborator type is unknown is not told
 * anything, since which side they are on cannot be established.
 */
const allocationOwnerAgainstViewer = (allocationWindow, collaboratorTypeID) => {
  if (!allocationWindow) return null;
  const type = Number(collaboratorTypeID);
  if (!Number.isFinite(type) || (type !== 1 && type !== 2)) return null;
  const allowBy = String(allocationWindow.allowLotAllocationBy || '');
  if (allowBy === 'Sponsor' && type === 2) return 'Sponsor';
  if (allowBy === 'CMO' && type === 1) return 'CMO';
  return null;
};

/*
 * The window reason, but only when every fact the message quotes is actually
 * held: the start PO status, the close date and the delivery date. A partial
 * configuration produces no tooltip rather than a sentence with gaps in it.
 */
const resolveWindowReason = (allocationWindow, collaboratorTypeID, now = Date.now()) => {
  const side = resolveWindowSide(allocationWindow, collaboratorTypeID);
  if (!side) return null;
  if (side.triggerStart !== 'poStatus' || side.startPOStatusID == null) return null;
  if (side.triggerEnd !== 'expectedDeliveryDateBefore' || !side.windowCloseDate) return null;
  if (!allocationWindow.deliveryDate) return null;

  const window = {
    startPOStatusID: side.startPOStatusID,
    startPOStatusText: side.startPOStatusText || null,
    daysBeforeDelivery: side.daysBeforeDelivery,
    windowCloseDate: side.windowCloseDate,
    deliveryDate: allocationWindow.deliveryDate,
    currentPOStatusID: allocationWindow.currentPOStatusID ?? null,
    currentPOStatusText: allocationWindow.currentPOStatusText || null,
  };

  /*
   * windowCloseDate is a pure calculation — delivery date minus the configured
   * number of days — so holding one says nothing about whether that date has
   * arrived. It has to be compared with today before the window can be called
   * closed, or a PO delivering in December is told its window "closed" on a
   * date still weeks away.
   */
  const closeTime = windowClosesAt(side.windowCloseDate);
  const isClosed = Number.isFinite(closeTime) && closeTime <= now;

  const current = asNum(allocationWindow.currentPOStatusID);
  const start = asNum(side.startPOStatusID);
  const hasOpened = current === null || start === null || current >= start;

  // Past the close date the window is over whether or not it ever opened, so
  // this is tested first — "opens at ..." would promise something that can no
  // longer happen.
  if (isClosed) {
    return { reason: LOCK_REASONS.WINDOW_CLOSED, checklist: null, missing: [], window };
  }
  // Before the start status the window has not opened yet. Saying it "closed
  // on" a date would be wrong, so the two are reported separately.
  if (!hasOpened) {
    return { reason: LOCK_REASONS.WINDOW_NOT_OPEN, checklist: null, missing: [], window };
  }

  /*
   * Started, and not yet past its close date: the window is OPEN right now, so
   * whatever is disabling the button, it is not this. Returning a window
   * message here stated something demonstrably false. Falls through to the
   * document check, and to no tooltip at all if that does not apply either —
   * an unexplained button is better than a confident wrong explanation.
   */
  return null;
};

/**
 * Resolve which tooltip to show, in the spec's priority order.
 * @returns {{reason, checklist, missing, window}|null} null when editing is
 *          allowed, or when no reason can be established from client-side data.
 */
/*
 * Why no tooltip appeared, for support. Silent unless someone opts in with
 *   localStorage.setItem('bcm891debug', '1')
 * in the browser console, so it costs nothing in normal use. Kept because the
 * inputs come from three different places — a role permission, a user's
 * associations and two product configurations — and a blank tooltip otherwise
 * gives no clue which one is missing.
 */
const debugLog = (result, inputs) => {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem('bcm891debug') !== '1') return;
    // eslint-disable-next-line no-console
    console.log('[BCM-891] tooltip reason:', result || 'NONE — button left bare', {
      canEditFields: inputs.canEditFields,
      hasRoleEditAccess: inputs.hasRoleEditAccess,
      po: inputs.po,
      associations: inputs.associations,
      allocationWindowPresent: !!inputs.allocationWindow,
      allocationWindow: inputs.allocationWindow,
      collaboratorTypeID: inputs.collaboratorTypeID,
      batchDocumentCount: (inputs.batchDocuments || []).length,
      batchNumber: inputs.batchNumber,
    });
  } catch (e) {
    /* never let diagnostics break the screen */
  }
};

export const resolveLockReason = (args = {}) => {
  const result = resolveLockReasonInner(args);
  debugLog(result && result.reason, args);
  return result;
};

const resolveLockReasonInner = ({
  canEditFields,
  hasRoleEditAccess,
  po = {},
  associations = {},
  allocationWindow = null,
  collaboratorTypeID = null,
  batchDocuments = [],
  batchNumber = null,
  // Injectable so "has the window closed yet?" can be tested against a fixed
  // instant instead of whenever the suite happens to run.
  now = Date.now(),
} = {}) => {
  if (canEditFields) return null;

  // 1. Role-level access is the only reason knowable without the associations
  //    call, and the spec ranks it first, so it short-circuits.
  if (!hasRoleEditAccess) {
    return { reason: LOCK_REASONS.ROLE_NO_EDIT, checklist: null, missing: [] };
  }

  const checklist = computeAccessChecklist(po, associations);
  const missing = ['cmo', 'brand', 'productType'].filter(
    (k) => checklist[k] === 'missing'
  );

  if (missing.length > 1) {
    return { reason: LOCK_REASONS.MULTIPLE_MISSING, checklist, missing };
  }
  // 2. Brand alone — the spec's wording ("you have access to its CMO, plant and
  //    product type, but not its brand") only holds when brand is the one gap.
  if (missing.length === 1 && missing[0] === 'brand') {
    return { reason: LOCK_REASONS.BRAND_MISSING, checklist, missing };
  }

  /*
   * A single non-brand gap — the CMO alone, or the product type alone. The spec
   * covers "brand alone" and "more than one" and says nothing about this, which
   * left a user whose product type had been removed looking at a disabled
   * button with no tooltip on it. It is the same kind of problem as the two the
   * spec does cover, so it is reported the same way, naming whichever
   * association is actually missing.
   */
  if (missing.length === 1) {
    return { reason: LOCK_REASONS.ASSOCIATION_MISSING, checklist, missing };
  }

  // 3. Nothing else applies yet, so the remaining cause in
  //    enableLotAllocation is the window. Reported only when the product's
  //    configuration supplies every fact the message quotes.
  const windowReason = resolveWindowReason(allocationWindow, collaboratorTypeID, now);
  if (windowReason) return windowReason;

  /*
   * 4. The product names the other party as the one who allocates.
   *
   * allowLotAllocationBy is the first term of enableLotAllocation, so when it
   * names the other side this IS the cause, not a guess — and it is why
   * resolveWindowReason returned nothing just above: the viewer's side has no
   * window of its own. Ranked before documents for exactly that reason.
   */
  const allocationOwner = allocationOwnerAgainstViewer(allocationWindow, collaboratorTypeID);
  if (allocationOwner) {
    return { reason: LOCK_REASONS.OWNED_BY_OTHER_SIDE, checklist: null, missing: [], allocationOwner };
  }

  /*
   * 4. Batch documents (spec reason 5), last as the spec orders it.
   *
   * enableLotAllocation — the rule that actually produces
   * editPlannedAllocation — contains no document term, so received documents
   * are never provably the cause. This is therefore only offered once nothing
   * else explains the lock: the role may edit, no association is missing, and
   * the product's window either is not configured or does not apply. In that
   * position it states a fact that is true (these documents were ingested, on
   * these dates) instead of leaving the button unexplained, and it can never
   * displace a reason that is known.
   */
  const docs = (Array.isArray(batchDocuments) ? batchDocuments : []).filter(
    (d) => !batchNumber || !d?.batchNumber || asText(d.batchNumber) === asText(batchNumber)
  );
  if (docs.length > 0) {
    return { reason: LOCK_REASONS.DOCUMENTS_RECEIVED, checklist: null, missing: [], documents: docs };
  }

  /*
   * 6. Nothing above applies, and the button is still disabled.
   *
   * This used to return null and leave the button bare, which is the thing
   * users actually report — a greyed-out control and no way to find out why.
   * Everything this case rules out is known for certain (the role may edit, no
   * association is missing, the window is open or absent, the product does not
   * hand allocation to the other side, no documents), so the message says that
   * much and points at the product's allocation setup without claiming which
   * part of it. An honest "an admin can check it" beats silence.
   */
  return { reason: LOCK_REASONS.UNKNOWN, checklist, missing: [] };
};
